import { useEffect, useRef, type ReactNode } from 'react'

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="empty" role="status">
      {label ?? 'Wird geladen …'}
    </div>
  )
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {hint && <div>{hint}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  )
}

export function Notice({ kind = 'info', children }: { kind?: 'info' | 'error' | 'success' | 'warn'; children: ReactNode }) {
  return (
    <div className={`notice ${kind}`} role={kind === 'error' ? 'alert' : undefined}>
      {children}
    </div>
  )
}

export function StatusBadge({ status }: { status: string }) {
  const label: Record<string, string> = {
    open: 'offen',
    done: 'erledigt',
    missed: 'nicht erledigt',
    final_missed: 'endgültig nicht erledigt',
    skipped: 'entfallen',
    in_progress: 'in Arbeit',
    waiting: 'Rückfrage',
    rejected: 'abgelehnt',
  }
  return <span className={`badge ${status}`}>{label[status] ?? status}</span>
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && !error && <div className="hint">{hint}</div>}
      {error && <div className="error">{error}</div>}
    </label>
  )
}

/**
 * A native <dialog>: real focus trapping and Escape handling for free, which a
 * hand-rolled overlay reliably gets wrong.
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog className="modal" ref={ref} onCancel={(event) => { event.preventDefault(); onClose() }}>
      <div className="card-head">
        <h2>{title}</h2>
        <button type="button" className="btn ghost small" onClick={onClose} aria-label="Schließen">
          ✕
        </button>
      </div>
      <div className="card-body">{children}</div>
      {footer && <footer>{footer}</footer>}
    </dialog>
  )
}

export function Bar({ value, total }: { value: number; total: number }) {
  const percent = total > 0 ? Math.round((value / total) * 100) : 0
  return (
    <div className="bar" role="img" aria-label={`${percent} Prozent`}>
      <i style={{ width: `${percent}%` }} />
    </div>
  )
}

export function Tile({
  label,
  value,
  sub,
  tone,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: 'alert' | 'good'
}) {
  return (
    <div className={`tile ${tone ?? ''}`}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  )
}
